import React, { useMemo, useState } from 'react';
import { Modal, Flex, Form, RadioChangeEvent } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Title } from 'components/Base/Typography';
import { FormItem } from 'components/Base/Form';
import Button from 'components/Base/Button/Button';
import { Select } from 'components/Base/Select';
import {
  ReportFileDownload,
  useLazyDownloadReportQuery,
} from 'redux/apiSlice/reporting.slice';
import { RadioGroup } from 'components/Base/RadioGroup';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';

interface ReportModalProps {
  open: boolean;
  onCancel: () => void;
  reportName?: string;
  data?: ReportFileDownload;
}

const reportOptions = [
  { value: 'AEYC', label: 'AEYC' },
  { value: 'RFK', label: 'RFK' },
  { value: 'ELC', label: 'ELC' },
  { value: 'ECCE', label: 'ECCE' },
];

export const DownloadReportModal: React.FC<ReportModalProps> = ({
  open,
  onCancel,
  reportName = 'Choose Report',
  data,
}) => {
  const { showToastNotification } = useShowToastNotification();

  const [form] = Form.useForm();
  const [triggerDownload, { isLoading }] = useLazyDownloadReportQuery();
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [subcategoryOptions, setSubcategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const businessSegment = Form.useWatch('businessSegment', form);
  const subcategory = Form.useWatch('subcategory', form);

  const filteredReportOptions = useMemo(() => {
    if (!businessSegment || !subcategory) {
      return [];
    }

    return (
      data?.dropdown_items
        ?.filter(
          item =>
            item.business_segment === businessSegment &&
            item.subcategory === subcategory,
        )
        .map(item => ({
          label: item.title,
          value: item.filename,
        })) || []
    );
  }, [data?.dropdown_items, businessSegment, subcategory]);

  const handleBusinessSegmentChange = (e: RadioChangeEvent) => {
    const segment = e.target.value;

    form.setFieldsValue({ reportName: undefined, subcategory: undefined });
    setSelectedReport(null);

    // get the subcategories from the dropdown_items based on the selected business segment
    const subcategories = Array.from(
      new Set(
        data?.dropdown_items
          ?.filter(item => item.business_segment === segment)
          ?.map(item => item.subcategory),
      ),
    ).map(category => ({
      label: category
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      value: category,
    }));

    if (subcategories) {
      setSubcategoryOptions(subcategories);
    } else {
      setSubcategoryOptions([]);
    }
  };

  const handleSubcategoryChange = () => {
    form.setFieldsValue({ reportName: undefined });
    setSelectedReport(null);
  };

  // Update selected report when form value changes
  const handleReportChange = (value: string) => {
    setSelectedReport(value);
  };

  const handleDownload = () => {
    form
      .validateFields()
      .then(values => {
        // Trigger the download
        triggerDownload(values.reportName)
          .unwrap()
          .then((blob: Blob | MediaSource) => {
            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor element to trigger the download
            const a = document.createElement('a');
            a.href = url;
            a.download = values.reportName;
            document.body.appendChild(a);
            a.click();

            // Clean up
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            // Close the modal
            onCancel();
          })
          .catch(() => {
            showToastNotification({
              message: 'Download failed. Please try again later.',
              type: ENotificationType.ERROR,
            });
          });
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <Modal
      title={<Title level={2}>{reportName}</Title>}
      open={open}
      onCancel={onCancel}
      width={800}
      footer={null}
      data-cy="report-modal"
      destroyOnClose
      centered
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        style={{ marginTop: 16 }}
      >
        <FormItem
          name="businessSegment"
          rules={[
            { required: true, message: 'Please Select Business Segment' },
          ]}
        >
          <RadioGroup
            optionType="button"
            buttonStyle="solid"
            options={reportOptions}
            onChange={handleBusinessSegmentChange}
            style={{ display: 'flex' }}
          />
        </FormItem>

        <FormItem
          label="Data Subcategory"
          name="subcategory"
          rules={[{ required: true, message: 'Please select a subcategory' }]}
          data-cy="report-modal-subcategory"
        >
          <Select
            placeholder="Select Subcategory"
            onChange={handleSubcategoryChange}
            options={subcategoryOptions}
            disabled={!businessSegment}
          />
        </FormItem>

        <FormItem
          label="Report Name"
          name="reportName"
          rules={[{ required: true, message: 'Please select a report name' }]}
          data-cy="report-modal-report-name"
          required
        >
          <Select
            placeholder="Select Report Name"
            onChange={handleReportChange}
            options={filteredReportOptions}
            disabled={!businessSegment}
          />
        </FormItem>
      </Form>

      <Flex justify="flex-end" gap={16} style={{ marginTop: 40 }}>
        <Button
          variant="default"
          onClick={onCancel}
          data-cy="report-modal-cancel"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          data-cy="report-modal-download"
          loading={isLoading}
          disabled={!selectedReport}
        >
          Download
        </Button>
      </Flex>
    </Modal>
  );
};

export default DownloadReportModal;
